import { useEffect, useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { extractErrorMessage } from '@/pages/painel-pedidos/lib/errors';
import {
  TEMPERATURA_INFO,
  ESTAGIO_RELACIONAMENTO_LABELS,
  CANAIS_LABELS,
  PONTAS_STATUS,
  PONTAS_STATUS_LABELS,
  RelacionamentoQuente,
  PontaSolta,
  Viagem,
} from '@/pages/painel/lib/inteligencia';
import { formatDate } from '@/pages/painel/lib/comercial';

interface QuemEncontrar {
  total: number;
  relacionamentos: any[];
}

export default function RelacionamentosModule() {
  const { password } = useOutletContext<{ password: string }>();
  const [tab, setTab] = useState<'quentes' | 'pontas' | 'viagens'>('quentes');

  const [quentes, setQuentes] = useState<RelacionamentoQuente[]>([]);
  const [pontas, setPontas] = useState<PontaSolta[]>([]);
  const [viagens, setViagens] = useState<Viagem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // viagem form
  const [cidade, setCidade] = useState('');
  const [uf, setUf] = useState('');
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');
  const [motivo, setMotivo] = useState('');

  // quem encontrar
  const [quemCidade, setQuemCidade] = useState('');
  const [quem, setQuem] = useState<QuemEncontrar | null>(null);
  const [buscando, setBuscando] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [q, p, v] = await Promise.all([
        supabase.functions.invoke('relacionamento-admin', { body: { action: 'relacionamentos_quentes', password } }),
        supabase.functions.invoke('relacionamento-admin', { body: { action: 'pontas_listar', password } }),
        supabase.functions.invoke('relacionamento-admin', { body: { action: 'viagens_listar', password } }),
      ]);
      const parse = (r: any) => (r.data?.error ? Promise.reject(new Error(r.data.error)) : r.data);
      const [qData, pData, vData] = await Promise.all([parse(q), parse(p), parse(v)]);
      setQuentes(qData?.relacionamentos ?? []);
      setPontas(pData?.pontas ?? []);
      setViagens(vData?.viagens ?? []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao carregar relacionamentos.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [password]);

  const salvarViagem = async () => {
    if (!cidade.trim()) return;
    try {
      await supabase.functions.invoke('relacionamento-admin', {
        body: { action: 'viagem_salvar', password, cidade: cidade.trim(), uf, data_inicio: dataInicio || null, data_fim: dataFim || null, motivo },
      });
      setCidade(''); setUf(''); setDataInicio(''); setDataFim(''); setMotivo('');
      await load();
    } catch { /* silencioso */ }
  };

  const excluirViagem = async (id: number) => {
    try { await supabase.functions.invoke('relacionamento-admin', { body: { action: 'viagem_excluir', password, id } }); await load(); } catch { /* silencioso */ }
  };

  const buscarQuem = async () => {
    if (!quemCidade.trim()) return;
    setBuscando(true);
    try {
      const { data, error: e } = await supabase.functions.invoke('relacionamento-admin', { body: { action: 'quem_encontrar', password, cidade: quemCidade.trim() } });
      if (e) throw new Error(await extractErrorMessage(e));
      if (data?.error) throw new Error(data.error);
      setQuem(data as QuemEncontrar);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao buscar.');
    } finally {
      setBuscando(false);
    }
  };

  const mudarPontaStatus = async (id: number, status: string) => {
    try { await supabase.functions.invoke('relacionamento-admin', { body: { action: 'ponta_status', password, id, status } }); await load(); } catch { /* silencioso */ }
  };

  const TABS = [
    { key: 'quentes' as const, label: 'Relacionamentos Quentes', icon: 'ri-fire-line' },
    { key: 'pontas' as const, label: 'Pontas soltas', icon: 'ri-links-line' },
    { key: 'viagens' as const, label: 'Viagens / Onde encontrar', icon: 'ri-map-pin-2-line' },
  ];

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 flex items-center justify-center bg-[#E8610A]/10 text-[#E8610A] rounded-xl">
          <i className="ri-heart-3-line text-lg"></i>
        </div>
        <div>
          <h2 className="font-bold text-gray-900">Relacionamentos</h2>
          <p className="text-sm text-gray-500">Memória de relacionamento omnichannel — a porta aberta não pode ser perdida.</p>
        </div>
      </div>

      {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3">{error}</div>}

      <div className="bg-white rounded-2xl border border-gray-200 px-2 py-2 flex items-center gap-1 overflow-x-auto">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold whitespace-nowrap transition-colors cursor-pointer ${tab === t.key ? 'bg-[#E8610A] text-white' : 'text-gray-600 hover:bg-gray-100'}`}
          >
            <i className={t.icon}></i>{t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><div className="w-8 h-8 border-2 border-[#E8610A] border-t-transparent rounded-full animate-spin" /></div>
      ) : (
        <>
          {/* RELACIONAMENTOS QUENTES */}
          {tab === 'quentes' && (
            quentes.length === 0 ? (
              <div className="bg-white rounded-2xl border border-dashed border-gray-200 py-12 px-6 text-center">
                <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-gray-50 text-gray-400 mb-3"><i className="ri-fire-line text-xl"></i></div>
                <p className="text-sm font-semibold text-gray-700">Nenhum relacionamento quente ainda</p>
                <p className="text-xs text-gray-400 mt-1 max-w-sm mx-auto">Classifique contatos como "quente" ou "muito quente" na ficha 360º. Quando os históricos reais forem importados, Celia Beatriz (Selinha) aparecerá aqui com reunião para remarcar.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                {quentes.map((r) => {
                  const temp = TEMPERATURA_INFO[r.temperatura];
                  const url = r.entidade_tipo === 'contato' ? `/painel/contatos/${r.contato_id}` : `/painel/clientes/${r.empresa_id}`;
                  return (
                    <div key={`${r.entidade_tipo}:${r.entidade_id}`} className="bg-white rounded-2xl border border-gray-200 p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="text-sm font-semibold text-gray-900">{r.nome}</p>
                            {r.alias && <span className="text-xs text-gray-500">“{r.alias}”</span>}
                          </div>
                          <div className="flex items-center gap-2 flex-wrap mt-1 text-xs text-gray-500">
                            {r.papel && <span>{r.papel}</span>}
                            {r.empresa && r.empresa !== r.nome && <span>{r.empresa}</span>}
                            {r.cidade && <span>{r.cidade}{r.uf ? `/${r.uf}` : ''}</span>}
                          </div>
                        </div>
                        {temp && <span className={`inline-flex items-center gap-1 text-[11px] font-bold rounded-full px-2 py-0.5 border ${temp.tone} shrink-0`}>{temp.emoji} {temp.label}</span>}
                      </div>

                      {r.estagio && (
                        <p className="text-xs text-gray-600 mt-2">Estágio: <span className="font-semibold">{ESTAGIO_RELACIONAMENTO_LABELS[r.estagio] ?? r.estagio}</span></p>
                      )}
                      {r.situacao && <p className="text-xs text-gray-600 mt-1">{r.situacao}</p>}
                      {r.pendencia && (
                        <div className="mt-2 text-xs bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                          <span className="text-amber-700 font-semibold">Pendência: </span>
                          <span className="text-amber-800">{r.pendencia}</span>
                        </div>
                      )}

                      <div className="flex items-center gap-3 flex-wrap mt-2 text-xs text-gray-500">
                        {r.ultimo_canal && <span><i className="ri-link"></i> {CANAIS_LABELS[r.ultimo_canal] ?? r.ultimo_canal}</span>}
                        {r.ultimo_contato && <span>Último contato {formatDate(r.ultimo_contato)}</span>}
                      </div>

                      {r.sem_proxima_acao && (
                        <div className="mt-2 text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-1.5">
                          <i className="ri-alert-line"></i> Relacionamento quente sem próximo passo.
                        </div>
                      )}

                      <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-100">
                        <Link to={url} className="inline-flex items-center gap-1 text-xs font-semibold text-[#E8610A] hover:text-[#C4500A] px-2.5 py-1.5 rounded-lg hover:bg-[#E8610A]/10 cursor-pointer whitespace-nowrap">
                          <i className="ri-arrow-right-up-line"></i> Abrir
                        </Link>
                        {r.proxima_acao && <span className="text-xs text-gray-600">Próxima ação: {r.proxima_acao}</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )
          )}

          {/* PONTAS SOLTAS */}
          {tab === 'pontas' && (
            pontas.length === 0 ? (
              <div className="bg-white rounded-2xl border border-dashed border-gray-200 py-12 px-6 text-center">
                <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-gray-50 text-gray-400 mb-3"><i className="ri-links-line text-xl"></i></div>
                <p className="text-sm font-semibold text-gray-700">Nenhuma ponta solta</p>
                <p className="text-xs text-gray-400 mt-1 max-w-sm mx-auto">Registre promessas e compromissos futuros ("me chama semana que vem", "vamos marcar um café") para não perdê-las.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {pontas.map((p) => (
                  <div key={p.id} className="bg-white rounded-xl border border-gray-200 p-4 flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm text-gray-800">{p.descricao}</p>
                        {p.contato_nome && (
                          <Link to={`/painel/contatos/${p.contato_id}`} className="text-xs font-semibold text-[#E8610A] hover:text-[#C4500A] cursor-pointer whitespace-nowrap">{p.contato_nome}</Link>
                        )}
                        {p.empresa_nome && !p.contato_nome && <span className="text-xs text-gray-500">{p.empresa_nome}</span>}
                      </div>
                      <p className="text-xs text-gray-400 mt-1">
                        {p.origem ? `${CANAIS_LABELS[p.origem] ?? p.origem} · ` : ''}
                        {p.data_conversa ? `${formatDate(p.data_conversa)} · ` : ''}
                        {p.janela_tempo ? `${p.janela_tempo} · ` : ''}
                        {p.proxima_acao ?? 'Sem próxima ação'}
                      </p>
                    </div>
                    <select value={p.status} onChange={(e) => mudarPontaStatus(p.id, e.target.value)} className="text-xs font-semibold border rounded-full px-2 py-1 cursor-pointer bg-white shrink-0">
                      {PONTAS_STATUS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                    </select>
                  </div>
                ))}
              </div>
            )
          )}

          {/* VIAGENS / ONDE ENCONTRAR */}
          {tab === 'viagens' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="space-y-4">
                <div className="bg-white rounded-2xl border border-gray-200 p-6">
                  <h3 className="text-sm font-bold text-gray-900 mb-4">Registrar janela de viagem / deslocamento</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <input value={cidade} onChange={(e) => setCidade(e.target.value)} placeholder="Cidade (ex.: Recife) *" className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:border-[#E8610A]" />
                    <input value={uf} onChange={(e) => setUf(e.target.value)} placeholder="UF (ex.: PE)" maxLength={2} className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:border-[#E8610A]" />
                    <input type="date" value={dataInicio} onChange={(e) => setDataInicio(e.target.value)} className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:border-[#E8610A]" />
                    <input type="date" value={dataFim} onChange={(e) => setDataFim(e.target.value)} className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:border-[#E8610A]" />
                    <input value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="Motivo (evento, reunião, viagem comercial)" className="sm:col-span-2 border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:border-[#E8610A]" />
                  </div>
                  <button onClick={salvarViagem} disabled={!cidade.trim()} className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-white bg-[#E8610A] hover:bg-[#C4500A] rounded-lg px-4 py-2 cursor-pointer whitespace-nowrap disabled:opacity-60"><i className="ri-add-line"></i> Salvar viagem</button>
                </div>

                <div className="bg-white rounded-2xl border border-gray-200 p-6">
                  <h3 className="text-sm font-bold text-gray-900 mb-1">Viagens registradas</h3>
                  {viagens.length === 0 ? (
                    <p className="text-sm text-gray-400">Nenhuma viagem registrada.</p>
                  ) : (
                    <div className="space-y-2">
                      {viagens.map((v) => (
                        <div key={v.id} className="flex items-center justify-between gap-3 border border-gray-100 rounded-xl p-3">
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-gray-800">{v.cidade}{v.uf ? `/${v.uf}` : ''}</p>
                            <p className="text-xs text-gray-400">{(v.data_inicio || v.data_fim) ? `${formatDate(v.data_inicio) || '—'} → ${formatDate(v.data_fim) || '—'}` : 'Sem data'}{v.motivo ? ` · ${v.motivo}` : ''}</p>
                          </div>
                          <button onClick={() => { setQuemCidade(v.cidade); setTab('viagens'); }} className="text-xs font-semibold text-[#E8610A] hover:text-[#C4500A] cursor-pointer whitespace-nowrap">Quem encontrar?</button>
                          <button onClick={() => excluirViagem(v.id)} className="text-gray-400 hover:text-red-600 cursor-pointer w-6 h-6 flex items-center justify-center"><i className="ri-delete-bin-line"></i></button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-white rounded-2xl border border-gray-200 p-6">
                <h3 className="text-sm font-bold text-gray-900 mb-1">Quem devo encontrar nessa viagem?</h3>
                <p className="text-xs text-gray-500 mb-4">Cruza relacionamento com deslocamento: contatos quentes, clientes, arquitetos, construtoras, oportunidades e reuniões pendentes na cidade.</p>
                <div className="flex gap-2 mb-4">
                  <input value={quemCidade} onChange={(e) => setQuemCidade(e.target.value)} placeholder="Cidade (ex.: Recife)" className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:border-[#E8610A]" />
                  <button onClick={buscarQuem} disabled={buscando || !quemCidade.trim()} className="inline-flex items-center gap-1.5 text-sm font-semibold text-white bg-gray-800 hover:bg-gray-700 rounded-lg px-4 py-2 cursor-pointer whitespace-nowrap disabled:opacity-60">
                    {buscando ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-search-line"></i>} Buscar
                  </button>
                </div>

                {quem && (
                  quem.total === 0 ? (
                    <p className="text-sm text-gray-400">Nenhum relacionamento estratégico encontrado em {quemCidade}.</p>
                  ) : (
                    <div className="space-y-2">
                      <p className="text-xs text-gray-500">{quem.total} relacionamento(s) em {quemCidade}.</p>
                      {quem.relacionamentos.map((r: any, i: number) => {
                        const temp = TEMPERATURA_INFO[r.temperatura];
                        const url = r.entidade_tipo === 'contato' ? `/painel/contatos/${r.contato_id}` : `/painel/clientes/${r.empresa_id}`;
                        return (
                          <div key={i} className="flex items-start justify-between gap-3 border border-gray-100 rounded-xl p-3">
                            <div className="min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <p className="text-sm font-semibold text-gray-900">{r.nome}</p>
                                {r.alias && <span className="text-xs text-gray-500">“{r.alias}”</span>}
                                {temp && <span className={`text-[10px] font-bold rounded-full px-1.5 py-0.5 ${temp.tone}`}>{temp.emoji} {temp.label}</span>}
                              </div>
                              <p className="text-xs text-gray-500 mt-0.5">{r.papel ?? ''}{r.estagio ? ` · ${ESTAGIO_RELACIONAMENTO_LABELS[r.estagio] ?? r.estagio}` : ''}</p>
                              <p className="text-xs text-gray-400 mt-0.5">{r.motivo}</p>
                            </div>
                            <Link to={url} className="text-xs font-semibold text-[#E8610A] hover:text-[#C4500A] cursor-pointer whitespace-nowrap shrink-0">Abrir</Link>
                          </div>
                        );
                      })}
                    </div>
                  )
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}